import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import {
  useInstallEligibility,
  markInstallPromptSeen,
} from '../hooks/useInstallEligibility';
import { useAndroidInstall } from '../hooks/useAndroidInstall';
import Button from '../components/Button';
import Icon from '../components/Icon';

export default function InstallPrompt() {
  const navigate = useNavigate();
  const ctx = useInstallEligibility();

  const continueToApp = () => {
    markInstallPromptSeen();
    navigate('/onboarding', { replace: true });
  };

  useEffect(() => {
    if (ctx === null) return; // still detecting
    if (ctx === 'standalone' || ctx === 'desktop' || ctx === 'other') {
      markInstallPromptSeen();
      navigate('/onboarding', { replace: true });
    }
  }, [ctx, navigate]);

  // null = detecting; ineligible contexts handled above — render nothing
  if (ctx === null || ctx === 'standalone' || ctx === 'desktop' || ctx === 'other') {
    return null;
  }

  return (
    <Shell>
      {ctx === 'ios-safari' && <IOSSafariInstall onContinue={continueToApp} />}
      {ctx === 'ios-other-browser' && <OpenInSafari onContinue={continueToApp} reason="other-browser" />}
      {ctx === 'ios-in-app' && <OpenInSafari onContinue={continueToApp} reason="in-app" />}
      {ctx === 'android' && <AndroidInstall onContinue={continueToApp} />}
      {ctx === 'android-in-app' && <OpenInChrome onContinue={continueToApp} />}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: colors.bg,
        color: colors.text,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px max(24px, env(safe-area-inset-left))',
        paddingTop: 'calc(40px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div style={{ width: '100%', maxWidth: 460, margin: '0 auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
}

function Hero() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', marginBottom: 28 }}>
      <div
        style={{
          width: 96, height: 96, margin: '0 auto 16px',
          backgroundColor: '#000', borderRadius: 22,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: `0 12px 32px ${colors.accent}33`,
        }}
      >
        <img src="/icons/icon-512.png" alt="" style={{ width: 78, height: 78, objectFit: 'contain' }} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, color: colors.text, letterSpacing: 1 }}>AGA</div>
      <div style={{ fontSize: 13, color: colors.textSub, marginTop: 2 }}>{t('install.tagline')}</div>
    </div>
  );
}

/* ---------------- iOS Safari: full install instructions ---------------- */

function IOSSafariInstall({ onContinue }: { onContinue: () => void }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  return (
    <>
      <Hero />

      <div style={{ fontSize: 22, fontWeight: 800, color: colors.text, textAlign: 'center', marginBottom: 8 }}>
        {t('install.ios.title')}
      </div>
      <div style={{ fontSize: 14, color: colors.textSub, textAlign: 'center', lineHeight: 1.55, marginBottom: 28 }}>
        {t('install.ios.subtitle')}
      </div>

      <Step number={1}>
        <Trans i18nKey="install.ios.step1" components={[<span />, <DotsIcon />, <span />, <strong />]} />
      </Step>
      <Step number={2}>
        <Trans i18nKey="install.ios.step2" components={[<span />, <ShareIcon />, <span />, <strong />]} />
      </Step>
      <Step number={3}>
        <Trans i18nKey="install.ios.step3" components={[<span />, <AddIcon />, <span />, <strong />, <span />, <strong />]} />
      </Step>

      <div
        style={{
          backgroundColor: colors.accentSoft,
          border: `1px solid ${colors.accentDark}33`,
          borderRadius: 12, padding: 14,
          marginTop: 12, marginBottom: 22,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}
      >
        <Icon name="lightbulb" size={14} color={colors.accentText} style={{ marginTop: 2 }} />
        <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55, color: colors.accentText, fontWeight: 600 }}>
          <Trans i18nKey="install.ios.tip" components={[<span />, <strong />]} />
        </div>
      </div>

      <BouncingArrow />

      <div style={{ flex: 1 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
        <Button label={t('install.continue')} icon="arrow-right" iconRight onClick={onContinue} />
        <button
          onClick={onContinue}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.textMuted, fontSize: 13, fontWeight: 600, padding: 12, minHeight: 44,
          }}
        >
          {t('install.continueWithout')}
        </button>
      </div>
    </>
  );
}

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        display: 'flex', gap: 14, alignItems: 'flex-start',
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14, padding: 16, marginBottom: 10,
      }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: '50%',
          backgroundColor: colors.accent, color: '#020617',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 900, fontSize: 14, flexShrink: 0,
        }}
      >
        {number}
      </div>
      <div style={{ flex: 1, fontSize: 14, lineHeight: 1.6, color: colors.text }}>
        {children}
      </div>
    </div>
  );
}

function DotsIcon() {
  const { colors } = useTheme();
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 5,
        backgroundColor: colors.surfaceMuted,
        border: `1px solid ${colors.border}`,
        verticalAlign: 'middle', margin: '0 2px',
        fontSize: 11, fontWeight: 900, letterSpacing: 1,
        color: colors.text,
      }}
    >
      ···
    </span>
  );
}

function ShareIcon() {
  const { colors } = useTheme();
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 5,
        backgroundColor: colors.surfaceMuted,
        border: `1px solid ${colors.border}`,
        verticalAlign: 'middle', margin: '0 2px',
      }}
    >
      <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.5 1V10" stroke="#0a84ff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M3 4L6.5 1L10 4" stroke="#0a84ff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 7H1V14H12V7H11" stroke="#0a84ff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function AddIcon() {
  const { colors } = useTheme();
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 5,
        backgroundColor: colors.surfaceMuted,
        border: `1px solid ${colors.border}`,
        verticalAlign: 'middle', margin: '0 2px',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1.5V10.5M1.5 6H10.5" stroke="#0a84ff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function BouncingArrow() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: '50%', bottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        animation: 'aga-bounce-down 1.4s ease-in-out infinite',
      }}
    >
      <div
        style={{
          padding: '10px 16px',
          borderRadius: 999,
          backgroundColor: colors.accent,
          color: '#020617',
          fontSize: 12, fontWeight: 800,
          boxShadow: `0 8px 22px ${colors.accent}66`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        {t('install.ios.bounce')} <Icon name="chevron-down" size={11} color="#020617" />
      </div>
      <style>{`
        @keyframes aga-bounce-down {
          0%, 100% { transform: translate(-50%, 0); }
          50%      { transform: translate(-50%, 8px); }
        }
      `}</style>
    </div>
  );
}

/* ---------------- iOS Chrome/Firefox + In-App Browser: redirect to Safari ---------------- */

function OpenInSafari({ onContinue, reason }: { onContinue: () => void; reason: 'other-browser' | 'in-app' }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const url = window.location.origin + '/';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('install.openInSafari.copiedToastTitle'), t('install.openInSafari.copiedToastMessage'));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t('install.openInSafari.copyFailedTitle'), t('install.openInSafari.copyFailedMessage'));
    }
  };

  const headline = reason === 'in-app' ? t('install.openInSafari.headlineInApp') : t('install.openInSafari.headlineOther');
  const subhead = reason === 'in-app'
    ? t('install.openInSafari.subheadInApp')
    : t('install.openInSafari.subheadOther');

  return (
    <>
      <Hero />

      <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, textAlign: 'center', marginBottom: 8 }}>
        {headline}
      </div>
      <div style={{ fontSize: 14, color: colors.textSub, textAlign: 'center', lineHeight: 1.55, marginBottom: 28 }}>
        {subhead}
      </div>

      <div
        style={{
          backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 14, padding: 18, marginBottom: 16,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.5, marginBottom: 8 }}>
          {t('install.webAddress')}
        </div>
        <div
          style={{
            fontSize: 14, fontFamily: 'Menlo, Courier, monospace',
            color: colors.text, wordBreak: 'break-all', lineHeight: 1.5, marginBottom: 16,
          }}
        >
          {url}
        </div>
        <Button
          label={copied ? t('install.openInSafari.copied') : t('install.copyLink')}
          icon={copied ? 'check' : 'copy'}
          variant={copied ? 'outline' : 'primary'}
          onClick={copyLink}
        />
      </div>

      <Step number={1}><Trans i18nKey="install.openInSafari.step1" components={[<span />, <strong />]} /></Step>
      <Step number={2}><Trans i18nKey="install.openInSafari.step2" components={[<span />, <strong />]} /></Step>
      <Step number={3}><Trans i18nKey="install.openInSafari.step3" components={[<span />, <strong />]} /></Step>

      <div style={{ flex: 1 }} />

      <button
        onClick={onContinue}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: colors.textMuted, fontSize: 13, fontWeight: 600,
          padding: 12, minHeight: 44, marginTop: 24,
        }}
      >
        {t('install.continueHere')}
      </button>
    </>
  );
}

/* ---------------- Android Chrome: one-tap install via beforeinstallprompt ---------------- */

function AndroidInstall({ onContinue }: { onContinue: () => void }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const { available, installing, install } = useAndroidInstall();
  const [waited, setWaited] = useState(false);

  // beforeinstallprompt can fire late (sometimes a few seconds after page load).
  // Give it a moment before we fall back to manual instructions.
  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleInstall = async () => {
    const outcome = await install();
    if (outcome === 'accepted') {
      toast.success(t('install.android.installedToastTitle'), t('install.android.installedToastMessage'));
      markInstallPromptSeen();
      // Don't auto-continue here — let user choose to open the installed app.
      // appinstalled fires asynchronously; user will likely close the tab.
    } else if (outcome === 'dismissed') {
      toast.info(t('install.android.dismissedToastTitle'), t('install.android.dismissedToastMessage'));
    } else {
      toast.error(t('install.android.unavailableToastTitle'), t('install.android.unavailableToastMessage'));
    }
  };

  return (
    <>
      <Hero />

      <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, textAlign: 'center', marginBottom: 8 }}>
        {t('install.android.title')}
      </div>
      <div style={{ fontSize: 14, color: colors.textSub, textAlign: 'center', lineHeight: 1.55, marginBottom: 28 }}>
        {t('install.android.subtitle')}
      </div>

      {available ? (
        <div
          style={{
            backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
            borderRadius: 14, padding: 18, marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 14,
          }}
        >
          <div
            style={{
              width: 56, height: 56, borderRadius: 14, backgroundColor: '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, overflow: 'hidden',
            }}
          >
            <img src="/icons/icon-512.png" alt="" style={{ width: 44, height: 44, objectFit: 'contain' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: colors.text }}>AGA</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{t('install.android.webApp')}</div>
          </div>
          <Button
            label={installing ? t('install.android.installing') : t('install.android.install')}
            loading={installing}
            onClick={handleInstall}
            fullWidth={false}
            style={{ minWidth: 100 }}
          />
        </div>
      ) : (
        <>
          {!waited ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div
                style={{
                  width: 28, height: 28, margin: '0 auto',
                  border: `3px solid ${colors.accentSoft}`, borderTopColor: colors.accentDark,
                  borderRadius: '50%', animation: 'aga-spin 0.7s linear infinite',
                }}
              />
              <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 12 }}>
                {t('install.android.preparing')}
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  backgroundColor: colors.warningSoft, border: `1px solid ${colors.warning}`,
                  borderRadius: 12, padding: 14, marginBottom: 16,
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}
              >
                <Icon name="info-circle" size={14} color={colors.warningText} style={{ marginTop: 2 }} />
                <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55, color: colors.warningText, fontWeight: 600 }}>
                  {t('install.android.manualNotice')}
                </div>
              </div>
              <Step number={1}>
                <Trans i18nKey="install.android.step1" components={[<span />, <strong />]} />
              </Step>
              <Step number={2}>
                <Trans i18nKey="install.android.step2" components={[<span />, <strong />, <span />, <strong />]} />
              </Step>
              <Step number={3}>
                <Trans i18nKey="install.android.step3" components={[<span />, <strong />]} />
              </Step>
            </>
          )}
        </>
      )}

      <div style={{ flex: 1 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
        {!available && waited && (
          <Button label={t('install.continue')} icon="arrow-right" iconRight onClick={onContinue} />
        )}
        <button
          onClick={onContinue}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.textMuted, fontSize: 13, fontWeight: 600,
            padding: 12, minHeight: 44,
          }}
        >
          {available ? t('install.android.maybeLater') : t('install.continueWithout')}
        </button>
      </div>
    </>
  );
}

/* ---------------- Android in-app browser: redirect to Chrome ---------------- */

function OpenInChrome({ onContinue }: { onContinue: () => void }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const url = window.location.origin + '/';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('install.openInChrome.copiedToastTitle'), t('install.openInChrome.copiedToastMessage'));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t('install.openInChrome.copyFailedTitle'), t('install.openInChrome.copyFailedMessage'));
    }
  };

  return (
    <>
      <Hero />

      <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, textAlign: 'center', marginBottom: 8 }}>
        {t('install.openInChrome.title')}
      </div>
      <div style={{ fontSize: 14, color: colors.textSub, textAlign: 'center', lineHeight: 1.55, marginBottom: 28 }}>
        {t('install.openInChrome.subtitle')}
      </div>

      <div
        style={{
          backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 14, padding: 18, marginBottom: 16,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.5, marginBottom: 8 }}>
          {t('install.webAddress')}
        </div>
        <div
          style={{
            fontSize: 14, fontFamily: 'Menlo, Courier, monospace',
            color: colors.text, wordBreak: 'break-all', lineHeight: 1.5, marginBottom: 16,
          }}
        >
          {url}
        </div>
        <Button
          label={copied ? t('install.openInChrome.copied') : t('install.copyLink')}
          icon={copied ? 'check' : 'copy'}
          variant={copied ? 'outline' : 'primary'}
          onClick={copyLink}
        />
      </div>

      <Step number={1}><Trans i18nKey="install.openInChrome.step1" components={[<span />, <strong />]} /></Step>
      <Step number={2}><Trans i18nKey="install.openInChrome.step2" components={[<span />, <strong />]} /></Step>
      <Step number={3}><Trans i18nKey="install.openInChrome.step3" components={[<span />, <strong />]} /></Step>

      <div style={{ flex: 1 }} />

      <button
        onClick={onContinue}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: colors.textMuted, fontSize: 13, fontWeight: 600,
          padding: 12, minHeight: 44, marginTop: 24,
        }}
      >
        {t('install.continueHere')}
      </button>
    </>
  );
}
